import { useState, useRef, forwardRef, useImperativeHandle } from "react";
import { GameIframe, GameIframeRef } from "@/components/blocks/game-iframe";
import { GameMetadata } from "./game-registry";
import { useAuth } from "@/providers/auth-provider";
import { RankManager } from "./rank-manager";
import { GameManager } from "./game-manager";
interface GameAdapterProps {
  game: GameMetadata;
  mode: "local" | "online";
  onGameEvent?: (event: any) => Promise<boolean>;
  className?: string;
}

export interface GameAdapterRef {
  sendMessageToGame: (message: any) => void;
}

export const GameAdapter = forwardRef<GameAdapterRef, GameAdapterProps>(
  ({ game, mode, onGameEvent, className }, ref) => {
    const { user } = useAuth();
    const gameIframeRef = useRef<GameIframeRef>(null);
    const [isLoading, setIsLoading] = useState(true);

    useImperativeHandle(ref, () => ({
      sendMessageToGame: sendMessageToGame,
    }));

    const sendMessageToGame = (message: any) => {
      if (gameIframeRef.current) {
        gameIframeRef.current.sendMessageToGame(message);
      }
    };

    const handleGameEvent = (event: any) => {
      // forward-log 이벤트 무시
      if (event.type === "forward-log") {
        return;
      }
      onGameEventHandler(event);
    };

    const onGameEventHandler = async (event: any) => {
      // 게임 이벤트 처리.
      console.log(`[GameAdapter:${game.id}] Game Event:`, event);

      // 이벤트를 상위 컴포넌트로 전달
      const isProcessed = onGameEvent ? await onGameEvent(event) : false;
      if (isProcessed) {
        return;
      }

      // 기본 이벤트 처리
      switch (event.id) {
        case "game.ready":
          setIsLoading(false);
          break;
        case "game.data":
          const result = await GameManager.handleEvent(game.id, event);
          sendMessageToGame({
            id: event.id,
            data: result,
          });
          break;
        case "game.rank": {
          const result = await RankManager.handleEvent(game.id, event, user);
          sendMessageToGame({
            id: event.id,
            data: result,
          });
          break;
        }

        default:
          console.warn("[GameAdapter] 알 수 없는 이벤트:", event);
          break;
      }
    };

    const gamePath =
      import.meta.env.VITE_GAME_URL +
      game.path +
      "/index.html" +
      (mode === "online" ? "?mode=online" : "");

    return (
      <div className={`relative ${className || "w-full h-full"}`}>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary/30 border-t-primary" />
          </div>
        )}
        <GameIframe
          ref={gameIframeRef}
          gamePath={gamePath}
          title={`${game.name} - ${mode}`}
          onGameEvent={handleGameEvent}
          className="w-full h-full border-0"
        />
      </div>
    );
  },
);

GameAdapter.displayName = "GameAdapter";
